import { environment } from "../../config/environment";
import React, { useState, useEffect, useCallback } from "react";
import Sidebar from "../Sidebar";
import { customFetch } from "../../utils/Auth";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import PatientManagementTable from "./PatientManagementTable";
import { formatDateLong } from "../Oncall/utils";

const PatientManagement = ({ setAuth, setUserOrg }) => {
    const [searchId, setSearchId] = useState("");
    const [searchedPatient, setSearchedPatient] = useState(null);
    const [isLoadingSearch, setIsLoadingSearch] = useState(false);
    const [allDoctors, setAllDoctors] = useState([]);
    const [doctorSearch, setDoctorSearch] = useState("");
    const [filteredDoctors, setFilteredDoctors] = useState([]);
    const [selectedDoctor, setSelectedDoctor] = useState(null);
    const [appointmentDate, setAppointmentDate] = useState("");
    const [patients, setPatients] = useState([]);
    const [isLoadingPatients, setIsLoadingPatients] = useState(false);
    const [selectedPatientIds, setSelectedPatientIds] = useState([]);
    const [isDeleting, setIsDeleting] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [confirmAction, setConfirmAction] = useState(null);

    // Fetch all doctors when component mounts
    useEffect(() => {
        (async () => {
            try {
                const response = await customFetch(
                    `${environment.apiUrl}/doctors/all-doctors`,
                    { method: "GET" }
                );
                const data = await response.json();
                setAllDoctors(Array.isArray(data) ? data : []);
            } catch (err) {
                console.error("Failed to fetch doctors:", err);
                setAllDoctors([]);
            }
        })();
    }, []);

    // Filter doctors as user types
    useEffect(() => {
        if (doctorSearch.trim()) {
            const search = doctorSearch.trim().toLowerCase();
            setFilteredDoctors(
                allDoctors.filter((doc) => {
                    const name = doc.user_name?.toLowerCase() || "";
                    const id = doc.user_id?.toLowerCase() || "";
                    const email = doc.user_email?.toLowerCase() || "";
                    return name.includes(search) || id.includes(search) || email.includes(search);
                })
            );
        } else {
            setFilteredDoctors([]);
        }
    }, [doctorSearch, allDoctors]);

    // Set date to today when doctor is selected
    useEffect(() => {
        if (selectedDoctor) {
            const today = new Date();
            const yyyy = today.getFullYear();
            const mm = String(today.getMonth() + 1).padStart(2, "0");
            const dd = String(today.getDate()).padStart(2, "0");
            setAppointmentDate(`${yyyy}-${mm}-${dd}`);
        }
    }, [selectedDoctor]);

    // Search patient by ID
    const handleSearchPatient = async (e) => {
        e.preventDefault();

        if (!searchId.trim()) {
            alert("Please enter a patient ID");
            return;
        }

        // Clear doctor/date selection when searching by patient ID
        setSelectedDoctor(null);
        setAppointmentDate("");
        setPatients([]);
        setSelectedPatientIds([]);

        setIsLoadingSearch(true);
        setSearchedPatient(null);

        try {
            const response = await customFetch(
                `${environment.apiUrl}/patients/id/${searchId.trim()}`,
                { method: "GET" }
            );
            const data = await response.json();

            if (data && Object.keys(data).length > 0 && !data.error) {
                setSearchedPatient(data);
            } else {
                setSearchedPatient({ error: "Patient not found" });
            }
        } catch (error) {
            setSearchedPatient({ error: "Failed to fetch patient data" });
        } finally {
            setIsLoadingSearch(false);
        }
    };


    // Fetch patients when doctor and date are selected
    const fetchPatients = useCallback(async () => {
        if (!selectedDoctor || !appointmentDate) {
            setPatients([]);
            return;
        }

        setIsLoadingPatients(true);
        try {
            const formattedDate = formatDateLong(appointmentDate);
            const response = await customFetch(
                `${environment.apiUrl}/patients/summary/by-doctor-and-date?doctor_id=${encodeURIComponent(
                    selectedDoctor.user_id
                )}&date_of_appt=${encodeURIComponent(formattedDate)}`,
                { method: "GET" }
            );
            const data = await response.json();
            setPatients(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error("Failed to fetch patients:", err);
            setPatients([]);
        } finally {
            setIsLoadingPatients(false);
        }
    }, [selectedDoctor, appointmentDate]);

    // Fetch patients when doctor or date changes
    useEffect(() => {
        fetchPatients();
    }, [fetchPatients]);

    // Delete single patient
    const handleDeletePatient = async (patientId) => {
        setConfirmAction(() => async () => {
            setShowConfirmModal(false);
            setIsDeleting(true);
            try {
                const response = await customFetch(
                    `${environment.apiUrl}/patients/delete`,
                    {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                        },
                        body: JSON.stringify({ patient_id: patientId }),
                    }
                );
                const data = await response.json();

                if (data.success) {
                    // Refresh the patients list if viewing filtered list
                    if (selectedDoctor && appointmentDate) {
                        fetchPatients();
                    }
                    // Clear searched patient if it was deleted
                    if (searchedPatient && searchedPatient.patient_id === patientId) {
                        setSearchedPatient(null);
                        setSearchId("");
                    }
                    // Clear selection
                    setSelectedPatientIds([]);
                } else {
                    alert(`Error deleting patient: ${data.error}`);
                }
            } catch (error) {
                alert(`Failed to delete patient: ${error.message}`);
            } finally {
                setIsDeleting(false);
            }
        });
        setShowConfirmModal(true);
    };

    // Bulk delete patients
    const handleBulkDelete = async () => {
        if (selectedPatientIds.length === 0) {
            alert("Please select at least one patient to delete");
            return;
        }

        setConfirmAction(() => async () => {
            setShowConfirmModal(false);
            setIsDeleting(true);
            try {
                const response = await customFetch(
                    `${environment.apiUrl}/patients/bulk-delete`,
                    {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                        },
                        body: JSON.stringify({ patient_ids: selectedPatientIds }),
                    }
                );
                const data = await response.json();

                if (data.success) {
                    setSelectedPatientIds([]);
                    // Refresh the patients list
                    fetchPatients();
                } else {
                    alert(`Error deleting patients: ${data.error}`);
                }
            } catch (error) {
                alert(`Failed to delete patients: ${error.message}`);
            } finally {
                setIsDeleting(false);
            }
        });
        setShowConfirmModal(true);
    };

    // Navigate date backward (previous day)
    const handlePreviousDay = () => {
        if (!appointmentDate) return;
        const currentDate = new Date(appointmentDate);
        currentDate.setDate(currentDate.getDate() - 1);
        const yyyy = currentDate.getFullYear();
        const mm = String(currentDate.getMonth() + 1).padStart(2, "0");
        const dd = String(currentDate.getDate()).padStart(2, "0");
        setAppointmentDate(`${yyyy}-${mm}-${dd}`);
        setSelectedPatientIds([]);
    };

    // Navigate date forward (next day)
    const handleNextDay = () => {
        if (!appointmentDate) return;
        const currentDate = new Date(appointmentDate);
        currentDate.setDate(currentDate.getDate() + 1);
        const yyyy = currentDate.getFullYear();
        const mm = String(currentDate.getMonth() + 1).padStart(2, "0");
        const dd = String(currentDate.getDate()).padStart(2, "0");
        setAppointmentDate(`${yyyy}-${mm}-${dd}`);
        setSelectedPatientIds([]);
    };

    return (
        <div className="flex">
            <div className="h-[100dvh]">
                <div className="flex h-[100%] fixed top-0">
                    <Sidebar setAuth={setAuth} setUserOrg={setUserOrg} />
                </div>
            </div>
            <div
                className="pl-[10px] pr-[10px] flex flex-col w-full overflow-x-hidden"
                style={{
                    marginLeft: "var(--sidebar-width, 250px)",
                    width: "calc(100% - var(--sidebar-width, 250px))",
                }}
            >
                <div className="flex flex-col w-full">
                    <div className="flex flex-col justify-center items-center w-full">
                        <h1 className="font-bold text-[28px] text-ktx-purple mt-[2rem]">
                            Patient Management
                        </h1>

                        {/* Search Options - Side by Side */}
                        <div className="w-full mt-8 grid grid-cols-1 lg:grid-cols-2 gap-8">
                            {/* Search by Patient ID Section */}
                            <div className="w-full">
                                <h2 className="text-xl font-semibold text-gray-800 mb-4">
                                    Search by Patient ID
                                </h2>
                                <form
                                    onSubmit={handleSearchPatient}
                                    className="flex flex-col space-y-4"
                                >
                                    <div className="flex gap-4 items-end">
                                        <div className="flex-1">
                                            <label
                                                htmlFor="searchId"
                                                className="block text-sm font-medium text-gray-700 mb-2"
                                            >
                                                Patient ID
                                            </label>
                                            <Input
                                                type="text"
                                                id="searchId"
                                                value={searchId}
                                                onChange={(e) => setSearchId(e.target.value)}
                                                placeholder="Enter Patient ID..."
                                            />
                                        </div>
                                        <Button
                                            type="submit"
                                            disabled={isLoadingSearch}
                                            className="bg-ktx-purple hover:bg-purple-700"
                                        >
                                            {isLoadingSearch ? "Searching..." : "Search"}
                                        </Button>
                                    </div>
                                </form>

                                {/* Display searched patient */}
                                {searchedPatient && (
                                    <div className="mt-4 bg-gray-50 border border-gray-200 rounded-lg p-4">
                                        {searchedPatient.error ? (
                                            <p className="text-red-600">{searchedPatient.error}</p>
                                        ) : (
                                            <div>
                                                <h3 className="text-lg font-semibold text-gray-800 mb-2">
                                                    Patient Details
                                                </h3>
                                                <table className="min-w-full text-sm">
                                                    <tbody>
                                                        <tr>
                                                            <td className="font-semibold pr-4 py-1">Patient ID:</td>
                                                            <td>{searchedPatient.patient_id}</td>
                                                        </tr>
                                                        <tr>
                                                            <td className="font-semibold pr-4 py-1">MRN:</td>
                                                            <td>{searchedPatient.mrn || "-"}</td>
                                                        </tr>
                                                        <tr>
                                                            <td className="font-semibold pr-4 py-1">Doctor:</td>
                                                            <td>{searchedPatient.doctor_name || "-"}</td>
                                                        </tr>
                                                        <tr>
                                                            <td className="font-semibold pr-4 py-1">Status:</td>
                                                            <td>{searchedPatient.status || "-"}</td>
                                                        </tr>
                                                        <tr>
                                                            <td className="font-semibold pr-4 py-1">Appointment Date:</td>
                                                            <td>{searchedPatient.date_of_appt || "-"}</td>
                                                        </tr>
                                                        <tr>
                                                            <td className="font-semibold pr-4 py-1">Appointment ID:</td>
                                                            <td>{searchedPatient.appointment_id || "-"}</td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                                <div className="mt-4">
                                                    <Button
                                                        onClick={() => handleDeletePatient(searchedPatient.patient_id)}
                                                        disabled={isDeleting}
                                                        className="bg-red-600 hover:bg-red-700 text-white"
                                                    >
                                                        {isDeleting ? "Deleting..." : "Delete Patient"}
                                                    </Button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Browse by Doctor and Date Section */}
                            <div className="w-full">
                                <h2 className="text-xl font-semibold text-gray-800 mb-4">
                                    Browse by Doctor and Date
                                </h2>
                                <div className="flex flex-col space-y-4">
                                    <div className="w-full">
                                        <label
                                            htmlFor="doctorSearch"
                                            className="block text-sm font-medium text-gray-700 mb-2"
                                        >
                                            Select Doctor
                                        </label>
                                        <input
                                            id="doctorSearch"
                                            type="text"
                                            autoComplete="off"
                                            value={doctorSearch}
                                            onChange={(e) => setDoctorSearch(e.target.value)}
                                            placeholder="Type to search for a doctor..."
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-ktx-purple focus:border-ktx-purple"
                                        />

                                        {/* Doctor dropdown */}
                                        {doctorSearch && filteredDoctors.length > 0 && (
                                            <div className="w-full max-h-56 overflow-y-auto border border-gray-200 rounded-md bg-white shadow z-10 mt-1">
                                                {filteredDoctors.map((doc) => (
                                                    <div
                                                        key={doc.user_id}
                                                        className={`px-4 py-2 cursor-pointer hover:bg-purple-100 ${selectedDoctor && selectedDoctor.user_id === doc.user_id
                                                            ? "bg-purple-200"
                                                            : ""
                                                            }`}
                                                        onClick={() => {
                                                            // Clear patient ID search when selecting a doctor
                                                            setSearchId("");
                                                            setSearchedPatient(null);
                                                            // Set the selected doctor
                                                            setSelectedDoctor(doc);
                                                            setDoctorSearch("");
                                                            setSelectedPatientIds([]);
                                                        }}
                                                    >
                                                        <div className="font-semibold text-gray-900">
                                                            {doc.user_name}{" "}
                                                            {doc.user_email && (
                                                                <span className="text-xs text-gray-500">
                                                                    ({doc.user_email})
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className="text-xs text-gray-500">{doc.user_id}</div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        {doctorSearch && filteredDoctors.length === 0 && (
                                            <div className="w-full px-4 py-2 text-gray-400 mt-1">
                                                No doctors found.
                                            </div>
                                        )}
                                    </div>

                                    {/* Date Selection */}
                                    {selectedDoctor && (
                                        <div className="w-full">
                                            <label
                                                htmlFor="appointmentDate"
                                                className="block text-sm font-medium text-gray-700 mb-2"
                                            >
                                                Appointment Date
                                            </label>
                                            <div className="flex items-center gap-2">
                                                <button
                                                    onClick={handlePreviousDay}
                                                    className="px-3 py-2 bg-gray-200 hover:bg-gray-300 rounded-md transition-colors"
                                                    title="Previous day"
                                                >
                                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                                    </svg>
                                                </button>
                                                <input
                                                    id="appointmentDate"
                                                    type="date"
                                                    value={appointmentDate}
                                                    onChange={(e) => {
                                                        setAppointmentDate(e.target.value);
                                                        setSelectedPatientIds([]);
                                                    }}
                                                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-ktx-purple focus:border-ktx-purple"
                                                />
                                                <button
                                                    onClick={handleNextDay}
                                                    className="px-3 py-2 bg-gray-200 hover:bg-gray-300 rounded-md transition-colors"
                                                    title="Next day"
                                                >
                                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                                    </svg>
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Patients List */}
                    {selectedDoctor && appointmentDate && (
                        <div className="w-full mt-8 mb-8">
                            <div className="flex justify-between items-center mb-4">
                                <h2 className="text-xl font-semibold text-gray-800">
                                    Patients for {selectedDoctor.user_name} on{" "}
                                    {new Date(appointmentDate + 'T00:00:00').toLocaleDateString('en-US', {
                                        year: 'numeric',
                                        month: 'long',
                                        day: 'numeric'
                                    })}
                                </h2>
                                <div className="flex gap-3">
                                    {selectedPatientIds.length > 0 && (
                                        <Button
                                            onClick={handleBulkDelete}
                                            disabled={isDeleting}
                                            className="bg-red-600 hover:bg-red-700 text-white"
                                        >
                                            {isDeleting
                                                ? "Deleting..."
                                                : `Delete Selected (${selectedPatientIds.length})`}
                                        </Button>
                                    )}
                                    <Button
                                        onClick={fetchPatients}
                                        disabled={isLoadingPatients}
                                        className="bg-ktx-purple hover:bg-purple-700"
                                    >
                                        {isLoadingPatients ? "Refreshing..." : "Refresh"}
                                    </Button>
                                </div>
                            </div>

                            <PatientManagementTable
                                patients={patients}
                                isLoading={isLoadingPatients}
                                selectedPatientIds={selectedPatientIds}
                                setSelectedPatientIds={setSelectedPatientIds}
                                onDeletePatient={handleDeletePatient}
                                isDeleting={isDeleting}
                            />
                        </div>
                    )}
                </div>
            </div>

            {/* Confirmation Modal */}
            {showConfirmModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                        <div className="flex items-center mb-4">
                            <svg className="w-6 h-6 text-red-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                            <h3 className="text-lg font-semibold text-gray-900">Confirm Deletion</h3>
                        </div>
                        <p className="text-gray-700 mb-6">
                            ⚠️ <strong>WARNING:</strong> This will PERMANENTLY delete the selected patient(s). This action CANNOT be undone. Are you absolutely sure?
                        </p>
                        <div className="flex justify-end gap-3">
                            <button
                                onClick={() => setShowConfirmModal(false)}
                                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-md transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmAction}
                                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md transition-colors"
                            >
                                Delete Permanently
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PatientManagement;
